/**
 * Import bans: tariff-code rows that exclude goods from importation outright
 * instead of adding a duty. The first are the Section 338 Canada bans that
 * take effect September 29, 2026 (dairy, motorcycles over 800 cc, alcoholic
 * beverages).
 *
 * A ban row is marked by bd_tariff_rate "BARRED". It has no Chapter 99
 * provision (bd_ch_99_code is the literal "NOT ALLOWED", shown as-is), and it
 * lists its HTS lines in base_hts_codes as 8- or 10-digit prefixes, leaving
 * bd_applies_to empty, which the duty pipeline reads as "every HTS code". So
 * ban rows are matched here instead, and must never reach the duty pipeline,
 * where "BARRED" would also parse to NaN.
 *
 * Gates: bd_barred_question / bd_barred_answer and
 * bd_importation_date_question / bd_importation_date_answer each pose a
 * yes/no question. The stored answer is the one that keeps the ban on; any
 * other answer lifts it, and a row with both gates needs both kept on. Gates
 * start at the stored answer, since these are prohibitions that apply until
 * the filer says otherwise, and a gate whose stored answer is missing can't
 * lift the ban at all.
 */

/** A tariff-code record as the datahub returns it. */
export type TariffCodeRow = Record<string, any>;

export type BanGateAnswer = 'yes' | 'no';

export type ImportBanGate = {
  /**
   * Key into the answers map. Built from the question, so a question several
   * rows share (the importation-date one is identical on every row) is asked
   * and answered once.
   */
  id: string;
  question: string;
  /** The answer that keeps the ban on, or null when the row's is missing. */
  keepOnAnswer: BanGateAnswer | null;
};

export type ImportBan = {
  id: string;
  /** tariff_list_name, e.g. "Section 338 Canada – Import Ban – Dairy". */
  measure: string;
  /** bd_ch_99_code as stored ("NOT ALLOWED"); not a Chapter 99 heading. */
  code: string;
  /** bd_rate_narrative, e.g. "Excluded from importation into the United States". */
  narrative: string;
  /** bd_article_description: the legal basis and effective date. */
  description: string;
  gates: ImportBanGate[];
};

export const IMPORT_BAN_RATE = 'BARRED';

export const isImportBanRecord = (row: TariffCodeRow): boolean =>
  String(row?.bd_tariff_rate ?? '')
    .trim()
    .toUpperCase() === IMPORT_BAN_RATE;

const text = (value: unknown): string => String(value ?? '').trim();

const toAnswer = (value: unknown): BanGateAnswer | null => {
  const answer = text(value).toLowerCase();
  return answer === 'yes' || answer === 'no' ? answer : null;
};

const listOf = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((item) =>
        text(item !== null && typeof item === 'object' ? item.code : item),
      )
    : text(value).split(',');

// Only 8- and 10-digit lines count. Anything else is malformed, and matching
// it as a prefix would bar every line under a whole heading or subheading.
const htsPrefixesOf = (row: TariffCodeRow): string[] =>
  [...listOf(row?.base_hts_codes), ...listOf(row?.bd_applies_to)]
    .map((code) => code.replace(/\D/g, ''))
    .filter((digits) => digits.length === 8 || digits.length === 10);

export const importBanCoversHts = (
  row: TariffCodeRow,
  htsCode: string,
): boolean => {
  const htsDigits = text(htsCode).replace(/\D/g, '');
  return (
    htsDigits.length > 0 &&
    htsPrefixesOf(row).some((prefix) => htsDigits.startsWith(prefix))
  );
};

const gatesOf = (row: TariffCodeRow): ImportBanGate[] => {
  const gates: ImportBanGate[] = [];
  const addGate = (kind: string, question: unknown, answer: unknown) => {
    const prompt = text(question);
    if (prompt) {
      gates.push({
        id: `${kind}:${prompt}`,
        question: prompt,
        keepOnAnswer: toAnswer(answer),
      });
    }
  };
  addGate('barred', row?.bd_barred_question, row?.bd_barred_answer);
  addGate(
    'importationDate',
    row?.bd_importation_date_question,
    row?.bd_importation_date_answer,
  );
  return gates;
};

const toImportBan = (row: TariffCodeRow, index: number): ImportBan => ({
  id:
    text(row?.bd_id_1) || text(row?.tariff_list_name) || `import-ban-${index}`,
  measure: text(row?.tariff_list_name) || 'Import ban',
  code: text(row?.bd_ch_99_code),
  // Trailing period dropped: the UI writes it as the first of two sentences.
  narrative:
    text(row?.bd_rate_narrative).replace(/\.+$/, '') ||
    'Excluded from importation',
  description: text(row?.bd_article_description),
  gates: gatesOf(row),
});

/**
 * The ban rows covering this HTS line. `inScope` applies the caller's origin
 * and effective-date checks, so they stay the same ones the duty pipeline
 * uses.
 */
export const matchImportBans = (
  rows: readonly TariffCodeRow[] | undefined,
  htsCode: string,
  inScope: (row: TariffCodeRow) => boolean,
): ImportBan[] =>
  (rows ?? [])
    .filter(
      (row) =>
        isImportBanRecord(row) &&
        importBanCoversHts(row, htsCode) &&
        inScope(row),
    )
    .map(toImportBan);

/** The filer's answer to a gate, else the stored one it starts at. */
export const importBanGateAnswer = (
  gate: ImportBanGate,
  answers: Record<string, BanGateAnswer>,
): BanGateAnswer | null => answers[gate.id] ?? gate.keepOnAnswer;

export const isImportBanActive = (
  ban: ImportBan,
  answers: Record<string, BanGateAnswer>,
): boolean =>
  ban.gates.every(
    (gate) =>
      gate.keepOnAnswer === null ||
      importBanGateAnswer(gate, answers) === gate.keepOnAnswer,
  );

export const findActiveImportBan = (
  bans: readonly ImportBan[],
  answers: Record<string, BanGateAnswer>,
): ImportBan | null =>
  bans.find((ban) => isImportBanActive(ban, answers)) ?? null;

/** The ban as its Duty Summary row, standing in for every other row. */
export const importBanResultRow = (
  ban: ImportBan,
): { type: string; code: string; rate: string } => ({
  type: ban.measure,
  code: ban.code,
  rate: IMPORT_BAN_RATE,
});
